import { supportedLanguages } from '../locales'

/**
 * Extract locale from URL pathname
 * Supports formats like:
 * - /en-us/preferences
 * - /nl-nl/preferences
 * - /nl-nl
 * - /preferences (returns null)
 */
export function getLocaleFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const pathname = window.location.pathname
  const match = pathname.match(/^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i)
  return match ? match[1].toLowerCase() : null
}

/**
 * Convert locale to language code
 * en-us -> en
 * nl-nl -> nl
 */
export function localeToLanguage(locale: string): string {
  return locale.split('-')[0]
}

/**
 * Get language from URL query param (for testing)
 */
export function getLanguageFromQueryParam(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('language')
}

/**
 * Language declared by the host document, e.g. <html lang="de-DE"> -> de.
 * This is what an embedded host renders itself in when the path carries no
 * locale — every in-store host, for example.
 */
export function getLanguageFromDocument(): string | null {
  if (typeof document === 'undefined') return null
  return localeToLanguage(document.documentElement.lang || '') || null
}

/**
 * A candidate only counts if we can actually translate the page into it.
 * Anything else falls through to the next candidate rather than leaving
 * `createI18n` to warn and fall back to English on every render.
 */
function toSupportedLanguage(candidate: string | null | undefined) {
  const language = candidate?.trim().toLowerCase()
  return language && supportedLanguages.includes(language) ? language : null
}

/**
 * Resolve the language the page is rendered in.
 *
 * This is the UI locale. It is not the language the customer wants their email
 * in — that one comes from the preferences API and drives the dropdown.
 *
 * Priority: host prop > query param > URL locale > host document > null
 *
 * The same chain runs standalone and embedded. A host that resolves its own
 * locale (customer hub) passes it in and wins; one that does not still gets the
 * storefront locale off the URL, or off <html lang> when the path has none.
 */
export function resolvePageLanguage(preferred?: string): string | null {
  // 1. Host told us explicitly
  const hostLanguage = toSupportedLanguage(preferred)
  if (hostLanguage) {
    return hostLanguage
  }

  // 2. Language query param (testing)
  const queryLanguage = toSupportedLanguage(getLanguageFromQueryParam())
  if (queryLanguage) {
    return queryLanguage
  }

  // 3. Storefront locale in the path, /de-de/...
  const locale = getLocaleFromUrl()
  const urlLanguage = locale
    ? toSupportedLanguage(localeToLanguage(locale))
    : null
  if (urlLanguage) {
    return urlLanguage
  }

  // 4. Whatever the host document says it is
  return toSupportedLanguage(getLanguageFromDocument())
}
